export type ChartType = 'bar' | 'horizontal_bar' | 'pie';
export type ChartVisibility = 'public' | 'private' | 'internal';

export type ChartDefinitionRow = {
  id: string;
  slug: string;
  title: string;
  teaser: string | null;
  field_key: string;
  /** Default / first format — kept in sync with chart_types[0]. */
  chart_type: ChartType;
  /** Allowed formats (1–3). Viewer can switch among these. */
  chart_types: ChartType[];
  audience_channel: string | null;
  audience_filter: Record<string, string>;
  visibility: ChartVisibility;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type ChartBucket = {
  label: string;
  count: number;
  percentage: number;
};

export type ChartAggregate = {
  chart: ChartDefinitionRow;
  fieldOptions: string[];
  audienceTotal: number;
  answered: number;
  unanswered: number;
  buckets: ChartBucket[];
};

export const CHART_TYPES: ChartType[] = ['horizontal_bar', 'bar', 'pie'];
export const CHART_VISIBILITIES: ChartVisibility[] = ['internal', 'private', 'public'];
export const MAX_CHART_TYPES = 3;

const SLUG_RE = /^[a-z][a-z0-9_]*$/;

export function normalizeChartSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function isValidChartSlug(slug: string): boolean {
  return SLUG_RE.test(slug) && slug.length >= 2 && slug.length <= 64;
}

export function parseAudienceFilter(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  }
  return out;
}

export function parseChartTypes(raw: unknown, fallback: ChartType): ChartType[] {
  const fromArray = Array.isArray(raw)
    ? raw.filter((t): t is ChartType => typeof t === 'string' && CHART_TYPES.includes(t as ChartType))
    : [];
  const unique: ChartType[] = [];
  for (const t of fromArray) {
    if (!unique.includes(t)) unique.push(t);
  }
  if (unique.length === 0) unique.push(fallback);
  return unique.slice(0, MAX_CHART_TYPES);
}

/** Normalize selection: 1–3 types; default is first. */
export function normalizeChartTypesSelection(
  selected: ChartType[],
  preferredDefault?: ChartType,
): ChartType[] {
  const unique: ChartType[] = [];
  for (const t of selected) {
    if (CHART_TYPES.includes(t) && !unique.includes(t)) unique.push(t);
  }
  if (unique.length === 0) unique.push('horizontal_bar');
  if (preferredDefault && unique.includes(preferredDefault)) {
    return [preferredDefault, ...unique.filter((t) => t !== preferredDefault)].slice(
      0,
      MAX_CHART_TYPES,
    );
  }
  return unique.slice(0, MAX_CHART_TYPES);
}

export function mapChartDefinitionRow(raw: Record<string, unknown>): ChartDefinitionRow {
  const chartTypeRaw = String(raw.chart_type ?? 'horizontal_bar');
  const chartType = (CHART_TYPES.includes(chartTypeRaw as ChartType)
    ? chartTypeRaw
    : 'horizontal_bar') as ChartType;
  const chartTypes = parseChartTypes(raw.chart_types, chartType);
  const ordered = chartTypes.includes(chartType)
    ? [chartType, ...chartTypes.filter((t) => t !== chartType)]
    : chartTypes;
  const visibility = String(raw.visibility ?? 'internal');
  return {
    id: String(raw.id),
    slug: String(raw.slug),
    title: String(raw.title),
    teaser: raw.teaser != null ? String(raw.teaser) : null,
    field_key: String(raw.field_key),
    chart_type: ordered[0] ?? chartType,
    chart_types: ordered,
    audience_channel: raw.audience_channel != null ? String(raw.audience_channel) : null,
    audience_filter: parseAudienceFilter(raw.audience_filter),
    visibility: (CHART_VISIBILITIES.includes(visibility as ChartVisibility)
      ? visibility
      : 'internal') as ChartVisibility,
    sort_order: typeof raw.sort_order === 'number' ? raw.sort_order : Number(raw.sort_order) || 0,
    active: raw.active !== false,
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function sortChartDefinitions(rows: ChartDefinitionRow[]): ChartDefinitionRow[] {
  return [...rows].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.title.localeCompare(b.title);
  });
}

export function chartTypeLabel(t: ChartType): string {
  switch (t) {
    case 'bar':
      return 'Bar';
    case 'horizontal_bar':
      return 'Horizontal bar';
    case 'pie':
      return 'Pie';
    default:
      return t;
  }
}

export function chartVisibilityLabel(v: ChartVisibility): string {
  switch (v) {
    case 'public':
      return 'Public';
    case 'private':
      return 'Private';
    case 'internal':
      return 'Internal';
    default:
      return v;
  }
}

/** Soft palette for pie slices (works on light + dark cards). */
export const CHART_SLICE_COLORS = [
  '#10b981',
  '#3b82f6',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#14b8a6',
  '#f97316',
  '#6366f1',
];

const ALLOWED_INLINE_TAGS = new Set(['b', 'strong', 'i', 'em', 'br']);

/**
 * Allow only simple inline tags used in select option labels (e.g. subscribe Q1).
 * Strips attributes and any other tags — safe for admin-authored field options.
 */
export function sanitizeInlineLabelHtml(raw: string): string {
  if (!raw) return '';
  // Normalize self-closing br
  let html = raw.replace(/<br\s*\/?>/gi, '<br/>');
  // Drop attributes on any tag
  html = html.replace(/<\/?([a-zA-Z0-9]+)(\s[^>]*)?>/g, (_m, tag: string, attrs?: string) => {
    const name = tag.toLowerCase();
    const closing = _m.startsWith('</');
    if (!ALLOWED_INLINE_TAGS.has(name)) return '';
    if (name === 'br') return '<br/>';
    if (closing) return `</${name}>`;
    // Ignore attrs entirely
    void attrs;
    return `<${name}>`;
  });
  return html;
}

/** Plain text for title/tooltip attributes. */
export function stripHtmlToText(raw: string): string {
  if (!raw) return '';
  return raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/?[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function readCustomFieldValue(customData: unknown, key: string): string {
  if (!customData || typeof customData !== 'object' || Array.isArray(customData)) return '';
  const v = (customData as Record<string, unknown>)[key];
  if (typeof v === 'string') return v.trim();
  if (v == null) return '';
  return String(v).trim();
}

function readCustomString(customData: unknown, key: string): string {
  return readCustomFieldValue(customData, key);
}

function subscriberInAudience(
  channels: unknown,
  customData: unknown,
  chart: ChartDefinitionRow,
): boolean {
  if (chart.audience_channel) {
    if (!channels || typeof channels !== 'object' || Array.isArray(channels)) return false;
    if ((channels as Record<string, unknown>)[chart.audience_channel] !== true) return false;
  }
  for (const [key, expected] of Object.entries(chart.audience_filter)) {
    if (readCustomString(customData, key) !== expected) return false;
  }
  return true;
}

/**
 * Aggregate one chart over a subscriber list (already loaded).
 * Options come from the field definition; unknown values are bucketed under "Other".
 */
export function aggregateChart(
  chart: ChartDefinitionRow,
  fieldOptions: string[],
  subscribers: Array<{ channels: unknown; custom_data: unknown }>,
): ChartAggregate {
  const audience = subscribers.filter((s) =>
    subscriberInAudience(s.channels, s.custom_data, chart),
  );
  const counts = new Map<string, number>();
  for (const opt of fieldOptions) counts.set(opt, 0);
  let other = 0;
  let answered = 0;

  for (const s of audience) {
    const value = readCustomString(s.custom_data, chart.field_key);
    if (!value) continue;
    answered += 1;
    if (counts.has(value)) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    } else {
      other += 1;
    }
  }

  const unanswered = audience.length - answered;
  const denom = answered > 0 ? answered : 1;

  const buckets: ChartBucket[] = fieldOptions.map((label) => {
    const count = counts.get(label) ?? 0;
    return {
      label,
      count,
      percentage: answered > 0 ? (count / denom) * 100 : 0,
    };
  });

  if (other > 0) {
    buckets.push({
      label: 'Other',
      count: other,
      percentage: answered > 0 ? (other / denom) * 100 : 0,
    });
  }

  return {
    chart,
    fieldOptions,
    audienceTotal: audience.length,
    answered,
    unanswered,
    buckets,
  };
}
